import { useState, useEffect } from 'react';
import { MOBILE_LAYOUT_MEDIA_QUERY } from '../constants/viewport';

/**
 * Subscribes to a CSS media query via matchMedia (preferred over resize + innerWidth).
 *
 * @param {string} query - e.g. '(max-width: 768px)'
 * @returns {boolean} Whether the query currently matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => getMediaQueryMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

function getMediaQueryMatches(query) {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * True when viewport width <= VIEWPORT_MOBILE_MAX_PX (same convention as NavigationBar sidebar hide).
 */
export function useIsMobileLayout() {
  return useMediaQuery(MOBILE_LAYOUT_MEDIA_QUERY);
}
